import React, { type JSX } from 'react';
import { CorporateFare } from '@mui/icons-material';
import { Avatar, Badge, Box, Card, List, Stack, Tooltip, Typography } from '@mui/material';
import { findAnglesOfTriangle } from 'utils/helpers';
import { makeStyles, createStyles } from '@mui/styles';
import { useNavigate, useParams } from 'react-router-dom';
import { useTypedSelector } from '@fiji/common';
import CustomIcon from 'components/CustomIcon';
import { selectedOrg } from '@fiji/common/src/features/orgManagement/orgSlice';

type AnimationStop = { top: number; left: number };

const EMPTY_KEYFRAME_BODY = {
    '0%': { top: 0, left: 0 },
    '100%': { top: 0, left: 0 },
};

const DIRECTIONAL_KEYFRAME_STOPS = {
    RECEIVER: {
        2: (positions: AnimationStop[]): Record<string, AnimationStop> => ({
            '100%': positions[0],
            '0%': positions[1],
        }),
        3: (positions: AnimationStop[]): Record<string, AnimationStop> => ({
            '100%': positions[0],
            '70%': positions[1],
            '0%': positions[2],
        }),
    },
    DEFAULT: {
        2: (positions: AnimationStop[]): Record<string, AnimationStop> => ({
            '0%': positions[0],
            '100%': positions[1],
        }),
        3: (positions: AnimationStop[]): Record<string, AnimationStop> => ({
            '0%': positions[0],
            '30%': positions[1],
            '100%': positions[2],
        }),
    },
};

const getDeviceTypeIds = (item: any): any[] => {
    if (item?.id === 'Other Assets') {
        return item?.assets?.map((asset: any) => asset?.deviceTypeId);
    }
    return [item?.id];
};

const resolveMultiWidgetDetailsPath = (realmName: string, deviceId?: string): string => {
    if (deviceId) {
        return `/${realmName}/energyFlowDetails/${deviceId}`;
    }
    return `/${realmName}/energyFlowDetails`;
};

const resolveSingleWidgetPath = (
    realmName: string,
    widgetWrapperType: string | undefined,
    selectedNode: any,
    currentOrg: any
): string => {
    if (widgetWrapperType === 'Gateway') {
        return `/${realmName}/device/${selectedNode?.id}#devices`;
    }
    return `/${realmName}/group/${selectedNode?.id ?? currentOrg?.id}#devices`;
};

const buildKeyframeStops = (
    positions: AnimationStop[],
    nodeType: string | undefined
): Record<string, AnimationStop> => {
    const directionKey = nodeType === 'RECEIVER' ? 'RECEIVER' : 'DEFAULT';
    const builders = DIRECTIONAL_KEYFRAME_STOPS[directionKey];
    const stopCountKey = positions.length as 2 | 3;
    const keyframeBuilder = builders[stopCountKey] ?? builders[3];
    return keyframeBuilder(positions);
};

const buildKeyframeEntry = (
    name: string,
    gridIndex: number | undefined,
    builder: () => Record<string, AnimationStop>
): Record<string, any> => {
    if (gridIndex === -1) {
        return { [`@keyframes ${name}`]: EMPTY_KEYFRAME_BODY };
    }
    return { [`@keyframes ${name}`]: builder() };
};

const TooltipAssetsList = ({ assets, unit }: { assets: any[]; unit: string }): JSX.Element => (
    <List disablePadding className="max-height-150 overflow-y-scroll">
        {assets?.map((subItem: any) => (
            <Stack p={2} key={subItem?.id} direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
                <Typography variant="body1" fontSize={'14px'}>
                    {subItem?.name ?? '--'}
                </Typography>
                <Stack direction={'row'} alignItems={'center'} spacing={1} pl={2}>
                    <Typography variant="body1" fontSize={'14px'} fontWeight={'400'}>
                        {subItem?.power ?? '--'}
                    </Typography>
                    <Typography variant="body1" fontSize={'14px'} fontWeight={'300'}>
                        {unit ?? '--'}
                    </Typography>
                </Stack>
            </Stack>
        ))}
    </List>
);

const TooltipCardContent = ({ item, onNavigate }: { item: any; onNavigate: () => void }): JSX.Element => (
    <Card sx={{ zIndex: 12 }}>
        <Stack
            p={2}
            className="border-bottom-1 cursor-pointer"
            direction={'row'}
            alignItems={'center'}
            justifyContent={'space-between'}
            onClick={onNavigate}
        >
            <Stack direction={'row'} alignItems={'center'} spacing={1}>
                {item.icon}
                <Stack>
                    <Typography variant="body1" fontSize={'14px'}>
                        {item?.displayName ?? '--'}
                    </Typography>
                </Stack>
            </Stack>
            <Stack justifyContent={'flex-start'} alignItems={'center'} pl={10}>
                <Typography variant="body2" fontSize={'14px'} fontWeight={'700'}>
                    {`${item?.value ?? '--'} ${item?.unit ?? '--'}`}
                </Typography>
            </Stack>
        </Stack>
        <TooltipAssetsList assets={item?.assets} unit={item?.unit} />
    </Card>
);

const buildPreviewWidgetIcon = (item: any): JSX.Element => {
    const iconWeb = item?.icon?.web;
    return (
        <Avatar className="text-white height-40 width-40" sx={{ backgroundColor: item?.activeColor }}>
            <CustomIcon iconName={iconWeb?.name} family={iconWeb?.family} />
        </Avatar>
    );
};

const mapAssetNodeToPreview = (item: any): any => {
    const source = item ?? {};
    return {
        id: source.id,
        count: source.count,
        icon: buildPreviewWidgetIcon(item),
        displayName: source.displayName,
        value: source.power,
        assets: source.assets,
        unit: source.unit,
        nodeType: source.type,
        activeColor: source.activeColor,
    };
};

const derivePreviewWidgetData = (widgetData: any): any[] | undefined =>
    widgetData?.secondary?.assetNode?.map(mapAssetNodeToPreview);

const deriveBuildingNode = (widgetData: any): any => widgetData?.secondary?.buildingNode;

const ConnectorTopBar = ({
    transformOrigin,
    activeColor,
}: {
    transformOrigin: string | undefined;
    activeColor: string;
}): JSX.Element | null => {
    if (!transformOrigin) return null;
    return (
        <Box
            className="w-25"
            sx={{
                borderTop: `2px solid ${activeColor}`,
                borderRadius: 12,
                position: 'relative',
                left: transformOrigin === 'left' ? '1px' : '-0.5px',
                top: transformOrigin === 'left' ? '2px' : '1.5px',
            }}
        />
    );
};

const ConnectorAnimatedDot = ({ show, className }: { show: boolean; className: string }): JSX.Element | null => {
    if (!show) return null;
    return (
        <Box
            sx={{
                position: 'absolute',
                height: '10px',
                width: '10px',
                borderRadius: '50%',
                backgroundColor: '#007bc1',
            }}
            className={className}
        />
    );
};

const buildConnectorLineSx = (
    transformOrigin: string | undefined,
    gridMeasuresState: any,
    itemStyleLineSx: any,
    activeColor: string
): any => {
    const base = {
        position: 'absolute',
        border: `2px solid ${activeColor}`,
    };
    if (!transformOrigin) return { ...base, ...itemStyleLineSx };
    const gridBase = gridMeasuresState.base ?? 0;
    return {
        ...base,
        [transformOrigin]: `${gridBase * 2}px`,
        width: `${gridBase}px`,
        top: `${gridMeasuresState.perpendicular / 2}px`,
        ...itemStyleLineSx,
    };
};

const EnergyFlowItemConnector = ({
    itemStyle,
    item,
    classes,
    dotClass,
    gridMeasuresState,
}: {
    itemStyle: any;
    item: any;
    classes: any;
    dotClass: string;
    gridMeasuresState: any;
}): JSX.Element => {
    const activeColor = item?.activeColor;
    const transformOrigin = itemStyle.lineSx?.transformOrigin;
    const showDot = item?.nodeType !== 'OFFLINE' && item?.nodeType !== 'NONE';
    return (
        <React.Fragment>
            <ConnectorTopBar transformOrigin={transformOrigin} activeColor={activeColor} />
            <ConnectorAnimatedDot show={showDot} className={classes[dotClass]} />
            <Box sx={buildConnectorLineSx(transformOrigin, gridMeasuresState, itemStyle.lineSx, activeColor)} />
        </React.Fragment>
    );
};

const OrganizationCenterBlock = ({ buildingNode }: { buildingNode: any }): JSX.Element => {
    const activeColor = buildingNode?.activeColor;
    const iconColor = activeColor === '#E0EEF8' ? '#007BC1' : '#fff';
    const textColor = activeColor === '#727E84' ? '#fff' : '#424E54';
    return (
        <Stack className="organization-wrapper" alignItems={'center'} justifyContent={'center'} sx={{ left: '33.33%' }}>
            <Stack className="organization" sx={{ backgroundColor: activeColor }}>
                <Stack alignItems={'center'} justifyContent={'center'} className="center">
                    <CorporateFare className="height-28 width-28" sx={{ color: iconColor }} />
                    <Stack direction={'row'} alignItems={'center'} spacing={1}>
                        <Typography fontSize={'12px'} fontWeight={'600'} sx={{ color: textColor }}>
                            {parseFloat(buildingNode?.power).toFixed(3)}
                        </Typography>
                        <Typography fontSize={'12px'} fontWeight={'300'} sx={{ color: textColor }}>
                            {buildingNode?.unit}
                        </Typography>
                    </Stack>
                </Stack>
            </Stack>
        </Stack>
    );
};

const EnergyFlowWidget = (props: any): JSX.Element => {
    const navigate = useNavigate();
    const { deviceId } = useParams();
    const currentRealmName = useTypedSelector((state) => state.common.selectedRealm);
    const gridRef = React.useRef<HTMLDivElement | null>(null);
    const { assetsCount, isAssetsTypeDisplayed, widgetData } = props;
    const selectedNode = useTypedSelector((state) => state.common.selectedNode);
    const widgetWrapperType = selectedNode?.wrapperDeviceType;
    const isMultiWidgetType = widgetData?.multiDevice;

    const currentOrg = useTypedSelector(selectedOrg);
    const previewWidgetData = derivePreviewWidgetData(widgetData);
    const buildingNode = deriveBuildingNode(widgetData);

    const sortAssetsWithOtherAtEnd = (assetsArray: any): any =>
        assetsArray.sort((currentAsset: any, nextAsset: any) => {
            if (currentAsset.id === 'Other Assets') return 1;
            if (nextAsset.id === 'Other Assets') return -1;
            return 0;
        });

    const [energyFlowWidgetData, setEnergyFlowWidgetData] = React.useState(
        sortAssetsWithOtherAtEnd(previewWidgetData)?.slice(0, assetsCount)
    );
    const [gridMeasuresState, setGridMeasuresState] = React.useState<any>({});
    const resizeComponent = (): void => {
        const gridMeasures = gridRef.current?.getBoundingClientRect();
        if (gridMeasures) {
            setGridMeasuresState(findAnglesOfTriangle(gridMeasures.height / 3, gridMeasures.width / 3));
        }
    };
    // Use a separate function to update energyFlowWidgetData based on assetsCount
    const updateEnergyFlowWidgetData = (count: any): void => {
        const newData = previewWidgetData?.slice(0, count); // Assuming initialData is your original data
        setEnergyFlowWidgetData(newData);
    };

    React.useEffect(() => {
        updateEnergyFlowWidgetData(assetsCount);
    }, [assetsCount, widgetData]);
    React.useEffect(() => {
        if (gridRef?.current) {
            resizeComponent();
            new ResizeObserver(resizeComponent).observe(gridRef.current);
        }
    }, [props]);

    const GRID_DATA_MAPPING: any = {
        '1': [2],
        '2': [2, 7],
        '3': [2, 6, 8],
        '4': [1, 3, 6, 8],
        '5': [1, 3, 4, 6, 8],
        '6': [1, 3, 4, 5, 6, 8],
        '7': [1, 2, 3, 4, 5, 6, 8],
        '8': [1, 2, 3, 4, 5, 6, 7, 8],
    };

    const useStyles = makeStyles(() => {
        const gridMapping = GRID_DATA_MAPPING[energyFlowWidgetData?.length.toString()];
        const findGridIndex = (n: number): number => gridMapping?.findIndex((item: any) => item === n);
        const firstGridIndex = findGridIndex(1);
        const secondGridIndex = findGridIndex(2);
        const thirdGridIndex = findGridIndex(3);
        const fourthGridIndex = findGridIndex(4);
        const fifthGridIndex = findGridIndex(5);
        const sixthGridIndex = findGridIndex(6);
        const seventhGridIndex = findGridIndex(7);
        const eightGridIndex = findGridIndex(8);

        return createStyles({
            dot1: {
                animation: `$firstDot 6s linear infinite`,
            },
            dot2: {
                animation: `$secondDot 6s linear infinite`,
            },
            dot3: {
                animation: `$thirdDot 6s linear infinite`,
            },
            dot4: {
                animation: `$fourthDot 6s linear infinite`,
            },
            dot5: {
                animation: `$fifthDot 6s linear infinite`,
            },
            dot6: {
                animation: `$sixthDot 6s linear infinite`,
            },
            dot7: {
                animation: `$seventhDot 6s linear infinite`,
            },
            dot8: {
                animation: `$eightDot 6s linear infinite`,
            },

            ...buildKeyframeEntry('firstDot', firstGridIndex, () => {
                const gridMeasures = gridRef.current?.getBoundingClientRect();
                const gridHeight = (gridMeasures?.height ?? 0) / 3;
                const gridWidth = (gridMeasures?.width ?? 0) / 3;

                const firstTop = gridHeight / 2 + 2;
                const firstLeft = gridWidth * (3 / 4);

                const secondTop = gridHeight / 2 + 2;
                const secondLeft = gridWidth;

                const thirdTop = gridHeight + gridHeight / 2;
                const thirdLeft = gridWidth + gridWidth / 2;

                return buildKeyframeStops(
                    [
                        { top: firstTop - 5, left: firstLeft - 5 },
                        { top: secondTop - 5, left: secondLeft - 5 },
                        { top: thirdTop - 5, left: thirdLeft - 5 },
                    ],
                    energyFlowWidgetData?.[firstGridIndex ?? 0]?.nodeType
                );
            }),
            ...buildKeyframeEntry('secondDot', secondGridIndex, () => {
                const gridMeasures = gridRef.current?.getBoundingClientRect();
                const gridHeight = (gridMeasures?.height ?? 0) / 3;
                const gridWidth = (gridMeasures?.width ?? 0) / 3;

                const firstTop = gridHeight / 2;
                const firstLeft = gridWidth * (3 / 4);

                const secondTop = gridHeight / 2;
                const secondLeft = gridWidth * (3 / 2);

                return buildKeyframeStops(
                    [
                        { top: firstTop - 5, left: firstLeft - 5 },
                        { top: secondTop - 5, left: secondLeft - 5 },
                    ],
                    energyFlowWidgetData?.[secondGridIndex ?? 0]?.nodeType
                );
            }),
            ...buildKeyframeEntry('thirdDot', thirdGridIndex, () => {
                const gridMeasures = gridRef.current?.getBoundingClientRect();
                const gridHeight = (gridMeasures?.height ?? 0) / 3;
                const gridWidth = (gridMeasures?.width ?? 0) / 3;

                const firstTop = gridHeight / 2 + 2;
                const firstLeft = gridWidth * (3 / 4);

                const secondTop = gridHeight / 2 + 2;
                const secondLeft = gridWidth;

                const thirdLeft = gridWidth + gridWidth / 2;
                const thirdTop = -gridHeight / 2;

                return buildKeyframeStops(
                    [
                        { top: firstTop - 5, left: firstLeft - 5 },
                        { top: secondTop - 5, left: secondLeft - 5 },
                        { top: thirdTop - 5, left: thirdLeft - 5 },
                    ],
                    energyFlowWidgetData?.[thirdGridIndex ?? 0]?.nodeType
                );
            }),
            ...buildKeyframeEntry('fourthDot', fourthGridIndex, () => {
                const gridMeasures = gridRef.current?.getBoundingClientRect();
                const gridHeight = (gridMeasures?.height ?? 0) / 3;
                const gridWidth = (gridMeasures?.width ?? 0) / 3;

                const firstTop = (gridHeight * 3) / 4;
                const firstLeft = gridWidth / 2;

                const secondTop = gridHeight * (3 / 2);
                const secondLeft = gridWidth / 2;

                return buildKeyframeStops(
                    [
                        { top: firstTop - 5, left: firstLeft - 6 },
                        { top: secondTop - 5, left: secondLeft - 5 },
                    ],
                    energyFlowWidgetData?.[fourthGridIndex ?? 0]?.nodeType
                );
            }),
            ...buildKeyframeEntry('fifthDot', fifthGridIndex, () => {
                const gridMeasures = gridRef.current?.getBoundingClientRect();
                const gridHeight = (gridMeasures?.height ?? 0) / 3;
                const gridWidth = (gridMeasures?.width ?? 0) / 3;

                const firstTop = gridHeight / 5;
                const firstLeft = gridWidth / 2;

                const secondTop = -gridHeight / 2;
                const secondLeft = gridWidth / 2;

                return buildKeyframeStops(
                    [
                        { top: firstTop - 5, left: firstLeft - 5 },
                        { top: secondTop - 5, left: secondLeft - 5 },
                    ],
                    energyFlowWidgetData?.[fifthGridIndex ?? 0]?.nodeType
                );
            }),
            ...buildKeyframeEntry('sixthDot', sixthGridIndex, () => {
                const gridMeasures = gridRef.current?.getBoundingClientRect();
                const gridHeight = (gridMeasures?.height ?? 0) / 3;
                const gridWidth = (gridMeasures?.width ?? 0) / 3;

                const firstTop = gridHeight / 2 + 2;
                const firstLeft = gridWidth / 4;

                const secondTop = gridHeight / 2 + 2;
                const secondLeft = 0;

                const thirdTop = gridHeight + gridHeight / 2;
                const thirdLeft = -gridWidth / 2;

                return buildKeyframeStops(
                    [
                        { top: firstTop - 5, left: firstLeft - 5 },
                        { top: secondTop - 5, left: secondLeft - 5 },
                        { top: thirdTop - 5, left: thirdLeft - 5 },
                    ],
                    energyFlowWidgetData?.[sixthGridIndex ?? 0]?.nodeType
                );
            }),
            ...buildKeyframeEntry('seventhDot', seventhGridIndex, () => {
                const gridMeasures = gridRef.current?.getBoundingClientRect();
                const gridHeight = (gridMeasures?.height ?? 0) / 3;
                const gridWidth = (gridMeasures?.width ?? 0) / 3;

                const firstTop = gridHeight / 2;
                const firstLeft = gridWidth / 4;

                const secondTop = gridHeight / 2;
                const secondLeft = -gridWidth / 2;

                return buildKeyframeStops(
                    [
                        { top: firstTop - 5, left: firstLeft - 5 },
                        { top: secondTop - 5, left: secondLeft - 5 },
                    ],
                    energyFlowWidgetData?.[seventhGridIndex ?? 0]?.nodeType
                );
            }),
            ...buildKeyframeEntry('eightDot', eightGridIndex, () => {
                const gridMeasures = gridRef.current?.getBoundingClientRect();
                const gridHeight = (gridMeasures?.height ?? 0) / 3;
                const gridWidth = (gridMeasures?.width ?? 0) / 3;

                const firstTop = gridHeight / 2 + 2;
                const firstLeft = gridWidth / 4;

                const secondTop = gridHeight / 2 + 2;
                const secondLeft = 0;

                const thirdTop = -(gridHeight / 2);
                const thirdLeft = -gridWidth / 2;

                return buildKeyframeStops(
                    [
                        { top: firstTop - 5, left: firstLeft - 5 },
                        { top: secondTop - 5, left: secondLeft - 5 },
                        { top: thirdTop - 5, left: thirdLeft - 5 },
                    ],
                    energyFlowWidgetData?.[eightGridIndex ?? 0]?.nodeType
                );
            }),
        });
    });

    const classes: any = useStyles();

    const STYLES_MAPPING = (item: any): object[] => [
        {
            sx: { left: 0 },
            className: 'widget-top left-top',
            direction: 'row',
            childJustifyContent: 'flex-end',
            lineSx: {
                transform: `skew(0deg, ${gridMeasuresState.alpha}deg)`,
                transformOrigin: 'left',
                borderRadius: '2px',
            },
        },
        {
            sx: { left: 0 },
            className: 'widget-center',
            direction: 'row',
            childJustifyContent: 'flex-end',
            lineSx: {
                width: `${(gridMeasuresState.base * 3) / 2}px`,
                borderTop: `1.5px solid ${item?.activeColor}`,
                left: '75%',
            },
        },
        {
            sx: { left: 0 },
            className: 'widget-bottom left-bottom',
            direction: 'row',
            childJustifyContent: 'flex-end',
            lineSx: { transform: `skew(0deg, ${-gridMeasuresState.alpha}deg)`, transformOrigin: 'left' },
        },
        {
            sx: { left: '33.33%' },
            className: 'widget-top',
            direction: 'column',
            justifyContent: 'center',
            lineSx: {
                top: `${gridMeasuresState.perpendicular * 0.8}px`,
                height: `${gridMeasuresState.perpendicular / 2}px`,
                border: `1.5px solid ${item?.activeColor}`,
            },
        },
        {
            sx: { left: '33.33%' },
            className: 'widget-bottom',
            direction: 'column',
            justifyContent: 'center',
            lineSx: {
                bottom: `${gridMeasuresState.perpendicular * 0.8}px`,
                height: `${gridMeasuresState.perpendicular / 2}px`,
                border: `1.5px solid ${item?.activeColor}`,
            },
        },
        {
            sx: { left: '66.66%' },
            className: 'widget-top right-top',
            direction: 'row-reverse',
            childJustifyContent: 'flex-start',
            lineSx: {
                transform: `skew(0deg, ${-gridMeasuresState.alpha}deg)`,
                transformOrigin: 'right',
                borderRadius: '16px',
            },
        },
        {
            sx: { left: '66.66%' },
            className: 'widget-center',
            direction: 'row-reverse',
            childJustifyContent: 'flex-start',
            lineSx: {
                width: `${(gridMeasuresState.base * 3) / 2}px`,
                borderTop: `1.5px solid ${item?.activeColor}`,
                right: '75%',
            },
        },
        {
            sx: { left: '66.66%' },
            className: 'widget-bottom right-bottom',
            direction: 'row-reverse',
            childJustifyContent: 'flex-start',
            lineSx: {
                transform: `skew(0deg, ${gridMeasuresState.alpha}deg)`,
                transformOrigin: 'right',
                borderRadius: '24px',
            },
        },
    ];

    const handleDeviceNavigation = (): string => {
        if (isMultiWidgetType) {
            return resolveMultiWidgetDetailsPath(currentRealmName, deviceId);
        }
        return resolveSingleWidgetPath(currentRealmName, widgetWrapperType, selectedNode, currentOrg);
    };

    return (
        <Stack className="energy-flow-wrapper-padding">
            <Stack className="energy-flow-wrapper" direction={'row'} ref={gridRef}>
                {energyFlowWidgetData?.map((item: any, index: any) => {
                    const itemStyleId = GRID_DATA_MAPPING[energyFlowWidgetData?.length.toString()][index];
                    const itemStyle: any = STYLES_MAPPING(item)[itemStyleId - 1];
                    const dotClass = `dot${itemStyleId}`;
                    return (
                        <Stack
                            key={item.id}
                            direction={itemStyle.direction}
                            alignItems={'center'}
                            justifyContent={itemStyle.justifyContent ?? 'space-between'}
                            className={itemStyle.className}
                            sx={itemStyle.sx}
                        >
                            <Stack
                                className="flex-70"
                                direction={'row'}
                                alignItems={'center'}
                                justifyContent={itemStyle.childJustifyContent}
                            >
                                <Tooltip
                                    placement="bottom"
                                    slotProps={{
                                        tooltip: {
                                            sx: {
                                                color: '#514E6A',
                                                backgroundColor: 'transparent',
                                            },
                                        },
                                    }}
                                    title={
                                        <TooltipCardContent
                                            item={item}
                                            onNavigate={(): void => {
                                                navigate(handleDeviceNavigation(), {
                                                    state: {
                                                        deviceTypeId: getDeviceTypeIds(item),
                                                        deviceIds: item?.assets?.map((asset: any) => asset?.id),
                                                    },
                                                });
                                            }}
                                        />
                                    }
                                >
                                    <Stack alignItems={'center'} justifyContent={'center'}>
                                        {isAssetsTypeDisplayed ? (
                                            <Badge badgeContent={item.count} className="custom-badge">
                                                {item.icon}
                                            </Badge>
                                        ) : (
                                            <>{item.icon}</>
                                        )}
                                        <Typography variant="caption" fontSize={'12px'}>
                                            {item?.displayName ?? '--'}
                                        </Typography>
                                        <Stack direction={'row'} alignItems={'center'} spacing={1}>
                                            <Typography variant="subtitle2" fontSize={'14px'} fontWeight={'600'}>
                                                {item.value}
                                            </Typography>
                                            <Typography fontWeight={'300'} fontSize={'14px'}>
                                                {item.unit}
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                </Tooltip>
                            </Stack>
                            <EnergyFlowItemConnector
                                itemStyle={itemStyle}
                                item={item}
                                classes={classes}
                                dotClass={dotClass}
                                gridMeasuresState={gridMeasuresState}
                            />
                        </Stack>
                    );
                })}
                <OrganizationCenterBlock buildingNode={buildingNode} />
            </Stack>
        </Stack>
    );
};

export default EnergyFlowWidget;
